import React, { useState, useMemo, useEffect, useContext } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { compose } from 'recompose';
import * as ROLES from '../../../constants/roles';
import Password from '../Password';
import AccountDetails from './AccountDetails';
import LoadPreferences from './LoadPreferences';
import Warehouse from './Warehouse';
import Documents from './Documents';
import Teammates from '../Teammates';
import TariffTable from '../TariffTable';
import AccountCardSetup from '../ShipperAccount/AccountCardSetup';
import QboRefs from './QboRefs';
import api from '@/utilities/api';
import * as Sentry from '@sentry/react';
import _ from 'lodash';

import { Tooltip } from '@material-ui/core';
import { Rating } from '@material-ui/lab';
import { UserContext } from '@/components/App';
import { LIST_TARIFFS_BY_CLIENT_ID, UPSERT_DRIVER_TARIFFS, UPSERT_PRICING_OVERRIDES } from '../Tariffs/graphql';
import { AGGREGATE_RATING_BY_REVIEWEE } from '@/graphql/queries/ratings';
import { useLazyQuery, useQuery, useMutation } from '@apollo/client';
import { UPDATE_CARRIER_DEFAULT_STATE_FILTER } from '../mutations';
import Stores from '../ShipperAccount/Stores';
import { TRUCKS_TAB, useTrucksTab } from '../hooks/useTrucksTab';
import ShippersTab from '../ShippersTab';
import ServiceLevels from '../ServiceLevels';
import CustomTags from '../CustomTags';
import CustomMessages from '../CustomMessages';
import Surveys from '../Surveys';
import CustomDocuments from '../CustomDocuments';
import { useClientUser } from '@/hooks';
import ArchiveDocuments from '../ArchiveDocuments';
import { TabCard } from '../blocks';
import { css } from '@emotion/react';
import MilestoneThresholds from '../MilestoneThresholds';
import EmailSubscriptions from '../EmailSubscriptions';
import PartnerCarriers from '../ShipperAccount/PartnerCarriers';
import SKUsAndServices from '../SKUsAndServices';
import CorpAdminStore, { CorpAdminContext } from '@/components/CorporateUsers/store';
import CompanyInfo from './CompanyInfo';
import Integrations from './Integrations';
import InsuranceContracts from './InsuranceContracts';
import CoverageCapacity from './CoverageCapacity';
import TariffLinks from './TariffLinks';
import ContractedCarriers from './ContractedCarriers';
import { isOrgFlexCenter } from '@/components/CorporateUsers/utils';
import { viewingOrganizationIdOf } from './utils';
import Snackbar from '@/components/Snackbar';

// The org tabs stay mounted together, so one Snackbar keeps a notice from showing once per tab.
const CorpAdminSnackbar = () => {
    const { state, callbacks } = useContext(CorpAdminContext);

    return (
        <Snackbar
            open={!!state.notification.message}
            {...state.notification}
            handleClose={() => callbacks.setNotification({})}
            autohide={4000}
        />
    );
};

function CarrierAccount() {
    const { user, sidebarCollapsed, client: currentCarrier, admin_clients } = useContext(UserContext);
    const { client_id, writableTariffClientIds, circles } = useClientUser();
    const organizationId = viewingOrganizationIdOf(user);
    const viewsOrgFlexCenter =
        !!organizationId &&
        !!admin_clients?.some((client) => client.client_id === client_id && isOrgFlexCenter(client));
    const onSelectTab = useTrucksTab();

    const [fetchTariffs, { data }] = useLazyQuery(LIST_TARIFFS_BY_CLIENT_ID, {
        variables: {
            client_ids: writableTariffClientIds,
        },
    });

    const [upsertDriverTariff] = useMutation(UPSERT_DRIVER_TARIFFS, {
        update: (cache, { data: { updated } }) => {
            cache.updateQuery(
                {
                    query: LIST_TARIFFS_BY_CLIENT_ID,
                    variables: {
                        client_ids: writableTariffClientIds,
                    },
                },
                (data) => {
                    let { driver_tariffs, ...rest } = data;
                    let clone = [...driver_tariffs];
                    const update = updated.returning[0];

                    const idx = clone.findIndex((m) => m.mapping_id === update.mapping_id);
                    if (idx >= 0) {
                        clone[idx] = update;
                    } else {
                        clone = [...clone, update];
                    }

                    return {
                        ...rest,
                        driver_tariffs: clone,
                    };
                }
            );
        },
    });

    const [upsertPo] = useMutation(UPSERT_PRICING_OVERRIDES, {
        update: (cache, { data: { updated } }) => {
            cache.updateQuery(
                {
                    query: LIST_TARIFFS_BY_CLIENT_ID,
                    variables: {
                        client_ids: writableTariffClientIds,
                    },
                },
                (data) => {
                    let { overrides, ...rest } = data;
                    let clone = [...overrides];
                    const [update] = updated;

                    const idx = clone.findIndex((po) => po.pricing_override_id === update.pricing_override_id);
                    if (idx >= 0) {
                        clone[idx] = update;
                    } else {
                        clone = [...clone, update];
                    }

                    return {
                        ...rest,
                        overrides: clone,
                    };
                }
            );
        },
        onError: (e) => {
            Sentry.captureException(e);
        },
    });

    const [fetchOverallRating, { data: ratings }] = useLazyQuery(AGGREGATE_RATING_BY_REVIEWEE);

    useEffect(() => {
        if (client_id) {
            fetchOverallRating({ variables: { reviewee_id: client_id } });
            fetchTariffs();
        }
    }, [client_id]);

    const [tariffs, overrides, driverMappings] = useMemo(() => {
        if (data) {
            const { tariffs, overrides, driver_tariffs } = data;
            return [tariffs, overrides, driver_tariffs];
        }

        return [[], [], []];
    }, [data]);

    const rating = useMemo(() => {
        if (ratings?.results?.aggregate?.avg?.rating) {
            return ratings?.results?.aggregate?.avg?.rating;
        }

        return 0;
    }, [ratings]);

    const accountTabs = (
        <Tabs defaultActiveKey="Account Details" onSelect={onSelectTab}>
            {organizationId && (
                <Tab tabClassName="tabLink" eventKey="Company Info" title="Company Info" mountOnEnter>
                    <CompanyInfo />
                </Tab>
            )}
            {organizationId && (
                <Tab tabClassName="tabLink" eventKey="Integrations" title="Integrations" mountOnEnter>
                    <Integrations />
                </Tab>
            )}
            {organizationId && (
                <Tab tabClassName="tabLink" eventKey="Insurance & Contracts" title="Insurance & Contracts" mountOnEnter>
                    <InsuranceContracts />
                </Tab>
            )}
            {organizationId && (
                <Tab tabClassName="tabLink" eventKey="Coverage + Capacity" title="Coverage + Capacity" mountOnEnter>
                    <CoverageCapacity />
                </Tab>
            )}
            {organizationId && (
                <Tab tabClassName="tabLink" eventKey="Tariff Links" title="Tariff Links" mountOnEnter>
                    <TariffLinks />
                </Tab>
            )}
            {viewsOrgFlexCenter && (
                <Tab tabClassName="tabLink" eventKey="Contracted Carriers" title="Contracted Carriers">
                    <ContractedCarriers key={client_id} />
                </Tab>
            )}
            <Tab tabClassName="tabLink" eventKey="Account Details" title="Account Details">
                <AccountDetails currentCarrierRef={client_id} currentCarrier={currentCarrier} user={user} />
            </Tab>
            <Tab tabClassName="tabLink" eventKey="Warehouse Details" title="Warehouse Details">
                <Stores currentClient={currentCarrier} />
            </Tab>
            <Tab tabClassName="tabLink" eventKey={TRUCKS_TAB} title="Trucks" />
            <Tab tabClassName="tabLink" eventKey="SKUs" title="SKU's / Services">
                <SKUsAndServices clientRef={client_id} />
            </Tab>
            <Tab tabClassName="tabLink" eventKey="Load Preferences" title="Load Preferences">
                <LoadPreferences currentCarrier={currentCarrier} />
            </Tab>
            <Tab tabClassName="tabLink" eventKey="Teammates" title="Teammates">
                <Teammates
                    teamLeaderType="carrier"
                    userId={client_id}
                    tariffs={tariffs}
                    driverMappings={driverMappings}
                    callbacks={{ upsertDriverTariff }}
                />
            </Tab>
            <Tab tabClassName="tabLink" eventKey="Custom Tags" title="Tags">
                <CustomTags />
            </Tab>
            <Tab tabClassName="tabLink" eventKey="Custom Documents" title="Custom Documents">
                <CustomDocuments />
                <ArchiveDocuments
                    client_id={client_id}
                    isAdmin={false}
                    editable={true}
                    allowedTypes={['ASSEMBLY', 'WAYBILL']}
                />
                <TabCard
                    css={css`
                        padding: 2rem;
                    `}
                >
                    <ArchiveDocuments client_id={client_id} isAdmin={true} editable={false} />
                </TabCard>
            </Tab>
            <Tab tabClassName="tabLink" eventKey="Service Types" title="Service Types">
                <ServiceLevels />
            </Tab>
            <Tab tabClassName="tabLink" eventKey="Shippers" title="Shippers">
                <ShippersTab />
            </Tab>
            <Tab tabClassName="tabLink" eventKey="Tariffs" title="Tariffs">
                <TariffTable
                    client={currentCarrier}
                    tariffs={tariffs}
                    overrides={overrides}
                    driverMappings={driverMappings}
                    callbacks={{ upsertPo, upsertDriverTariff }}
                />
            </Tab>
            <Tab tabClassName="tabLink" eventKey="Messages" title="Messages">
                <CustomMessages />
            </Tab>
            <Tab tabClassName="tabLink" eventKey="Surveys" title="Surveys">
                <Surveys />
            </Tab>
            <Tab tabClassName="tabLink" eventKey="Documents" title="Documents">
                <Documents currentCarrier={currentCarrier} />
            </Tab>
            <Tab tabClassName="tabLink" eventKey="Billing" title="Billing">
                <AccountCardSetup currentClient={currentCarrier} />
                <QboRefs currentClient={currentCarrier} />
            </Tab>
            {circles?.['carrier-milestone-thresholds'] && (
                <Tab tabClassName="tabLink" eventKey="Milestone Thresholds" title="Milestone Thresholds">
                    <MilestoneThresholds client={currentCarrier} />
                </Tab>
            )}
            <Tab tabClassName="tabLink" eventKey="Email Subscriptions" title="Email Subscriptions">
                <EmailSubscriptions client={currentCarrier} />
            </Tab>
            <Tab tabClassName="tabLink" eventKey="Partner Carriers" title="Partner Carriers">
                <PartnerCarriers />
            </Tab>
            <Tab tabClassName="tabLink" eventKey="Password" title="Password">
                <Password />
            </Tab>
        </Tabs>
    );

    return (
        <div className={`${sidebarCollapsed && 'collapse-margin'} sidenav-margin-responsive`}>
            <div className="d-flex justify-content-between my-3">
                <h2 className="  accountwidth" style={{ fontWeight: '700' }}>
                    My Account
                </h2>
                {rating >= 3 && (
                    <div className="align-self-center d-flex">
                        <h5 className="text-nowrap">My Rating: </h5>
                        <Tooltip
                            title={rating.toFixed(1) + ' / 5'}
                            arrow
                            PopperProps={{ style: { marginTop: '-10px' } }}
                        >
                            <div>
                                <Rating readOnly value={rating} precision={0.1} />
                            </div>
                        </Tooltip>
                    </div>
                )}
            </div>
            {organizationId ? (
                <CorpAdminStore organizationId={organizationId}>
                    <CorpAdminSnackbar />
                    {accountTabs}
                </CorpAdminStore>
            ) : (
                accountTabs
            )}
        </div>
    );
}

export default CarrierAccount;
